import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getDateForView, getFormattedDate } from "./utility/getFormattedDate";
import { useEffect, useState } from "react";
import { getCapitalizationListById } from "./utility/getCapitalizationListById";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { apiEndPoint } from "./utility/endPoint";
import { AddToHomeScreenEntry } from "./pwaComponents/AddToHomeScreenEntry";
import { baseWhite, mainTextColor } from "./utility/colorPalette";
import { useAuth } from "../context/AuthContext";
import { getAssistantList } from "./utility/getAssistantList";
import { DebugPage } from "./DebugPage";

const version = "ver2.3";

interface capitalizationDataType {
  userId: string;
  conversationId: string;
  date: string;
  complete: boolean;
  threadId?: string;
}

export const MainPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticator((context) => [context.user]);
  const { idToken, refreshAuthSession } = useAuth();
  const todaysDate: string = getFormattedDate();

  const [conversationList, setConversationList] = useState<
    capitalizationDataType[]
  >([]);

  const [newCapitalizationButtonViewIsOn, setNewCapitalizationButtonViewIsOn] =
    useState<boolean>(false);

  // 最初の会話データ0の表示用
  const [isNoDate, setIsNoData] = useState<boolean>(false);

  // 環境確認用
  const [isDev, setIsDev] = useState<boolean>(false);

  const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);

  useEffect(() => {
    console.log(user.userId);
    if (idToken) {
      console.log(idToken);
      getCapitalizationListById(user.userId, idToken).then((data) => {
        // console.log(data);
        setConversationList(data);

        if (data.length === 0) {
          setIsNoData(true);
          setNewCapitalizationButtonViewIsOn(true);
        } else {
          // 会話一覧の中に本日のデータが無い場合のみ新しい会話を開始できるようにする
          const isTodaysConversationData = data.some(
            (capitalization: capitalizationDataType) =>
              capitalization.date === todaysDate
          );
          if (!isTodaysConversationData) {
            setNewCapitalizationButtonViewIsOn(true);
          }
        }

        setIsLoadingComplete(true);
      });
    }

    // デバッグ確認用
    if (
      apiEndPoint !==
      "https://fl8u1qo3e4.execute-api.ap-northeast-1.amazonaws.com/dev"
    ) {
      setIsDev(true);
    }

    // // 通知の許可を取得
    // Notification.requestPermission();
    // registerPushSubscription().then((subscription) => {
    //   putPushSubscriptionData({
    //     userId: user.userId,
    //     subscription: subscription,
    //   });
    // });
  }, []);

  // はじめるボタンの処理（新しい会話）
  const handleStartNewConversation = () => {
    // navigate("/pre-questionnaire", {
    //   state: { userId: user.userId, date: todaysDate },
    // });
    navigate("/remindEvent");
  };

  // 過去の会話へ飛ぶ
  const viewPastCapitalization = (
    capitalizationData: capitalizationDataType
  ) => {
    // とりあえず仮仕様として、過去の日付の場合はcomplete扱いとする。
    // EventBridgeでcompleteにした方がちゃんとしている気はする。
    const completeState: boolean =
      capitalizationData.date !== todaysDate
        ? true
        : capitalizationData.complete;

    navigate("/chat", {
      state: {
        conversationId: capitalizationData.conversationId,
        conversationDate: capitalizationData.date,
        complete: completeState,
        threadId: capitalizationData.threadId,
      },
    });
  };

  //
  const getDateForCapitalizationList = (yyyymmdd: string) => {
    if (yyyymmdd === todaysDate) return "きょう";
    else return getDateForView(yyyymmdd);
  };

  const testFunc = () => {
    if (!idToken) return;

    getAssistantList(idToken).then((data) => {
      console.log(data);
    });
  };

  return (
    <div style={{ textAlign: "center" }}>
      <header
        style={{
          textAlign: "center",
          alignContent: "center",
          backgroundColor: baseWhite,
        }}
      >
        <Typography variant="body2" sx={{ mb: 0 }} component="div">
          {getDateForView(todaysDate)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ mt: 0, mr: 1, textAlign: "end" }}
          component="div"
        >
          {version}
        </Typography>
      </header>
      <Divider />

      {isDev && (
        <Typography variant="h3" color="red">
          現在開発環境です！
        </Typography>
      )}

      <Box sx={{ height: "3rem" }} />

      {!isLoadingComplete && (
        <Box>
          <Typography variant="body2" color={mainTextColor}>
            データを読み込んでいます
          </Typography>
          <CircularProgress />
        </Box>
      )}

      {isLoadingComplete && (
        <Box>
          {conversationList.length > 0 && (
            <div>
              {conversationList.map((capitalizationData) => (
                <>
                  {capitalizationData.complete === false &&
                    capitalizationData.date === todaysDate && (
                      <div
                        key={capitalizationData.conversationId}
                        onClick={() =>
                          viewPastCapitalization(capitalizationData)
                        }
                        style={{
                          height: "100px",
                          backgroundColor: baseWhite,
                          margin: "10px",
                          borderRadius: "5px",
                        }}
                      >
                        <Typography>
                          {getDateForCapitalizationList(
                            capitalizationData.date
                          )}
                          の会話
                        </Typography>

                        <Box
                          display="flex"
                          sx={{
                            padding: "0.5rem",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <InfoOutlineIcon fontSize="small" />
                          <Typography variant="body2">
                            未完了のチャットがあります
                          </Typography>
                        </Box>
                      </div>
                    )}
                </>
              ))}
            </div>
          )}

          {isNoDate && (
            <Box sx={{ padding: "2rem" }}>
              <Typography>データはまだありません</Typography>
            </Box>
          )}

          {newCapitalizationButtonViewIsOn && (
            // <Button variant="contained" onClick={handleStartNewConversation}>
            <Box>
              <Typography color={mainTextColor}>
                以下のボタンからはじめてください
              </Typography>
              <Button variant="contained" onClick={handleStartNewConversation}>
                はじめる
              </Button>
            </Box>
          )}

          {!newCapitalizationButtonViewIsOn &&
            conversationList.filter(
              (conversation) => conversation.date === todaysDate
            )[0].complete && (
              <Box>
                <Typography>きょうもありがとうございました。</Typography>
                <Typography>明日もよろしくお願いします。</Typography>
              </Box>
            )}
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <AddToHomeScreenEntry />
      </Box>

      {/* <Box
        sx={{ marginTop: "0.5rem", display: "flex", justifyContent: "center" }}
      >
        <DebugPage />
      </Box> */}
      {/* {idToken && <Button onClick={testFunc}>TEST</Button>} */}
    </div>
  );
};
